import { Formik, Form, Field } from "formik";
import { useState } from "react";
import { Col, Form as BTSForm, Modal, Row } from "react-bootstrap";
import * as yup from "yup";
import GradientButton from "../../../Core/GradientButton";
import AlertModal from "../../../Core/AlertModal";
import OneVoucherLogo from "../../../../Images/1Voucher.svg";
import { useSessionStore } from "../../../../Stores/SessionStore";

const validationSchema = yup
	.object({
		VoucherCode: yup.string().trim().required("1Voucher code is required."),
	})
	.required();

export default function OneVoucherModal({ show, onClose, category, product, voucher, amount, onSuccess }) {
	const store = useSessionStore();
	const [sending, setSending] = useState(false);
	const [error, setError] = useState(null);
	const [successAlert, setSuccessAlert] = useState(null);

	async function handleSubmit(values) {
		const customer = store.Session?.Customer;
		if (!customer) {
			setError("Your session has expired. Please sign in again.");
			return;
		}

		setError(null);
		setSending(true);
		const result = await store.RechargesService.RedeemVoucher(
			customer.Id,
			product.Id,
			voucher?.Id,
			values.VoucherCode.trim(),
			amount
		);
		setSending(false);

		if (result?.Success && result.Data) {
			const newPin = result.Data.NewPin?.trim();
			const hasChangePin = !!newPin && newPin !== "PIN_NOT_PROVIDED";
			const remainingBalance = result.Data.RemainingBalance;

			onClose();
			setSuccessAlert({
				show: true,
				success: true,
				title: "Success",
				message: hasChangePin ? (
					<div className="text-center">
						<p>Your new 1Voucher pin:</p>
						<p className="h3 my-2">{newPin}</p>
						{remainingBalance != null && remainingBalance > 0 && (
							<p>Remaining balance: R {remainingBalance.toFixed(2)}</p>
						)}
						<p>You will also receive this pin via SMS. Please keep it safe.</p>
						<p>
							We suggest you take a screenshot of this pin or write it down as you will not be able to view it again once you press OK or close this screen.
						</p>
					</div>
				) : (
					<div className="text-center">
						<p>Your payment was successful. Your 1Voucher was used in full and no change voucher was issued.</p>
					</div>
				),
				onClose: () => {
					setSuccessAlert(null);
					if (onSuccess) onSuccess();
				},
			});
			return;
		}

		setError(result?.Message ?? "An error occurred while redeeming your 1Voucher.");
	}

	return (
		<>
			<Modal show={show} onHide={onClose} size="lg">
				<Modal.Header>
					<div className="w-100 text-center">
						<img src={OneVoucherLogo} alt="1Voucher" style={{ height: 44, width: "auto" }} />
					</div>
				</Modal.Header>
				<Modal.Body>
					<div className="text-center bg-white p-3 mb-3">
						<p className="mb-0">
							Buy your 1Voucher at any Flash-enabled store, then enter your voucher code below to continue.
						</p>
					</div>
					<p className="text-center font-weight-bold">
						{category} {product.Name} Access (R {amount.toFixed(0)})
					</p>
					<Formik initialValues={{ VoucherCode: "" }} validationSchema={validationSchema} onSubmit={handleSubmit}>
						{({ errors, touched, handleSubmit: submit }) => (
							<Form>
								<BTSForm.Group as={Row} className="mb-3">
									<Col sm={12}>
										<Field
											as={BTSForm.Control}
											name="VoucherCode"
											inputMode="numeric"
											maxLength={16}
											placeholder="Enter your 1Voucher code"
											isInvalid={touched.VoucherCode && errors.VoucherCode}
										/>
										<small className="text-danger">{touched.VoucherCode && errors.VoucherCode}</small>
									</Col>
								</BTSForm.Group>
								{error && <small className="text-danger d-block mb-3">{error}</small>}
								<GradientButton
									title={sending ? "Verifying..." : "Submit"}
									variant="greenYellow"
									onClick={() => submit()}
									disabled={sending}
									loading={sending}
								/>
								<GradientButton title="Cancel Transaction" variant="yellowOrange" onClick={onClose} disabled={sending} className="mt-2" />
							</Form>
						)}
					</Formik>
				</Modal.Body>
			</Modal>
			{successAlert && <AlertModal modal={successAlert} />}
		</>
	);
}
